import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, authApi, formatApiError } from "@/lib/auth";
import { CreditCard, Check, ArrowRight, Loader2, Radar, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [currency, setCurrency] = useState("usd");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    authApi.get("/billing/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  // If returning from Stripe with session_id, poll status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const { data } = await authApi.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(interval);
          setPolling(false);
          setPollResult({ ok: true, ...data });
          await refreshUser();
          setTimeout(() => navigate("/app", { replace: true }), 1500);
        } else if (data.status === "expired" || attempts > 10) {
          clearInterval(interval);
          setPolling(false);
          setPollResult({ ok: false, ...data });
        }
      } catch (e) {
        if (attempts > 10) {
          clearInterval(interval);
          setPolling(false);
          setPollResult({ ok: false, error: formatApiError(e) });
        }
      }
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [location.search]);

  const startCheckout = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await authApi.post("/billing/checkout", {
        currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setSubmitting(false);
    }
  };

  const active = user?.subscription_status === "active" || user?.subscription_status === "trialing";

  return (
    <div className="min-h-screen bg-[#050505] grain text-white py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2.5 mb-8 group" data-testid="billing-brand">
          <div className="relative w-9 h-9 border border-[#EAB308] flex items-center justify-center bg-black">
            <Radar className="w-5 h-5 text-[#EAB308]" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-base font-black tracking-tighter uppercase">
            Card<span className="text-[#EAB308]">Radar</span>
          </div>
        </Link>

        {polling && (
          <div className="bg-[#0d0d0d] border border-[#EAB308]/40 p-6 rounded-sm mb-6 text-center" data-testid="polling-state">
            <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin mx-auto mb-3" />
            <div className="font-bold uppercase tracking-wider text-sm">Confirming your payment…</div>
            <div className="text-xs text-[#A3A3A3] mt-1">Stripe is finalising. This usually takes a few seconds.</div>
          </div>
        )}

        {pollResult?.ok && (
          <div className="bg-[#22C55E]/10 border border-[#22C55E]/40 p-6 rounded-sm mb-6" data-testid="payment-success">
            <Check className="w-8 h-8 text-[#22C55E] mb-2" />
            <div className="font-bold uppercase tracking-wider">Payment confirmed</div>
            <div className="text-sm text-[#A3A3A3] mt-1">You're now CardRadar Pro. Redirecting to your dashboard…</div>
          </div>
        )}

        {pollResult && pollResult.ok === false && (
          <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 p-6 rounded-sm mb-6" data-testid="payment-failed">
            <AlertCircle className="w-8 h-8 text-[#EF4444] mb-2" />
            <div className="font-bold uppercase tracking-wider">Payment not completed</div>
            <div className="text-sm text-[#A3A3A3] mt-1">Status: {pollResult.status || pollResult.error || "unknown"}. You can try again below.</div>
          </div>
        )}

        {active ? (
          <div className="bg-[#0d0d0d] border border-[#22C55E]/40 p-8 rounded-sm" data-testid="subscription-active">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-[#22C55E]" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#22C55E]">ACTIVE</span>
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tighter uppercase mb-2">
              You're <span className="text-[#22C55E]">Pro</span>.
            </h1>
            <p className="text-sm text-[#A3A3A3] mb-6">
              {user?.current_period_end ? (
                <>Access renews/expires on <strong className="text-white font-mono">{new Date(user.current_period_end).toLocaleDateString()}</strong>.</>
              ) : "Subscription active."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/app"
                data-testid="go-dashboard-btn"
                className="bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2"
              >
                Open dashboard <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={startCheckout}
                disabled={submitting}
                data-testid="renew-btn"
                className="border border-[#262626] hover:border-[#A3A3A3] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-sm transition-all"
              >
                {submitting ? "Loading…" : "Extend +30 days"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-black tracking-tighter uppercase mb-2">
              Unlock Card<span className="text-[#EAB308]">Radar</span> Pro
            </h1>
            <p className="text-sm text-[#A3A3A3] mb-8">
              30-day access pass. Renew when you want. Cancel = don't renew. No traps.
            </p>

            <div className="bg-[#0d0d0d] border-2 border-[#EAB308] p-6 rounded-sm shadow-[0_0_40px_rgba(234,179,8,0.15)]" data-testid="pricing-card">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-heading text-2xl font-black uppercase tracking-tighter">CardRadar Pro</h3>
                <CreditCard className="w-5 h-5 text-[#EAB308]" />
              </div>

              <div className="flex gap-2 mt-4" data-testid="currency-toggle">
                {(pricing?.currencies || [{ code: "USD", amount: 200 }, { code: "AUD", amount: 310 }]).map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCurrency(c.code.toLowerCase())}
                    data-testid={`currency-${c.code.toLowerCase()}`}
                    className={`flex-1 px-3 py-2 text-xs uppercase tracking-wider font-bold border rounded-sm transition-all ${
                      currency === c.code.toLowerCase()
                        ? "border-[#EAB308] text-[#EAB308] bg-[#EAB308]/10"
                        : "border-[#262626] text-[#737373] hover:border-[#A3A3A3] hover:text-white"
                    }`}
                  >
                    {c.code}
                  </button>
                ))}
              </div>

              <div className="flex items-baseline gap-2 mb-1 mt-4">
                <span className="font-mono text-5xl font-black text-white">
                  {currency === "usd" ? "$" : "A$"}
                  {(pricing?.currencies?.find((c) => c.code.toLowerCase() === currency)?.amount || (currency === "usd" ? 200 : 310)).toFixed(0)}
                </span>
                <span className="text-sm text-[#A3A3A3] font-mono">/ 30 days</span>
              </div>

              <div className="space-y-2 mt-6 mb-6">
                {[
                  "Unlimited watchlists + alerts",
                  "All 15 retailers (AU + US)",
                  "Scalp mode + encrypted vaults",
                  "Discord + Telegram webhooks",
                  "Windows .exe packaging",
                  "Cancel anytime — just don't renew",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-[#D4D4D4]">
                    <Check className="w-4 h-4 text-[#22C55E] shrink-0" />
                    {f}
                  </div>
                ))}
              </div>

              {error && (
                <div className="text-xs text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/30 px-3 py-2 rounded-sm mb-3">
                  {error}
                </div>
              )}

              <button
                onClick={startCheckout}
                disabled={submitting}
                data-testid="checkout-btn"
                className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-sm py-3 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_0_18px_rgba(234,179,8,0.25)]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {submitting ? "Redirecting to Stripe…" : "Subscribe now"}
              </button>
              <p className="text-center text-[10px] font-mono text-[#737373] mt-3">
                Secured by Stripe · {currency.toUpperCase()} card
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
